import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, NgForm, Validators } from '@angular/forms';
import { PlanStepType } from '@core/models/built/plan-step-type.enum';
import { DataCollectorTarget } from '@core/models/data-collector-target.enum';
import { Plan, PlanStep } from '@core/models/plan.model';
import { ProcedureStep } from '@core/models/procedures.model';
import { PlanCoreService } from '@core/services/features/plan-core.service';
import { ProcedureCoreService } from '@core/services/features/procedure-core.service';
import { EnhancedAutocompleteComponent } from '@enhanced-autocomplete';
import { FlowchartMode } from '@flowchart';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataCollectorConfig } from 'src/app/modules/core/models/data-collector-config.model';

import it from './i18n/data-collector-edit.it.json';

interface LocalFormValue {
  plan?: Plan;
  planStep?: PlanStep;
  procedureStep: ProcedureStep;
  key: string;
  target: DataCollectorTarget;
}

@Component({
  selector: 'data-collector-edit',
  templateUrl: './data-collector-edit.component.html',
  styles: [
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataCollectorEditComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('localFormEl') localFormEl!: NgForm;

  @ViewChild('plan') planCtl!: EnhancedAutocompleteComponent<Plan>;
  @ViewChild('planStep') planStepCtl!: EnhancedAutocompleteComponent<PlanStep>;
  @ViewChild('procedureStep') procedureStepCtl!: EnhancedAutocompleteComponent<ProcedureStep>;

  @Input() label?: string;
  @Input() panelLabel?: string;
  @Input() configs: DataCollectorConfig[] = [];
  @Input() mode: FlowchartMode = 'edit';
  @Input() currentPlanId?: number;
  @Input() currentPlanStepId?: number;
  @Input() currentProcedureId?: number;
  @Input() currentProcedureStepId?: number;

  @Output() configAdded = new EventEmitter<DataCollectorConfig>();
  @Output() configRemoved = new EventEmitter<number>();

  private configs$$ = new BehaviorSubject<DataCollectorConfig[]>(this.configs || []);
  configs$ = this.configs$$.asObservable();

  targets = Object.keys(DataCollectorTarget);

  planSearchFn = (name?: string) => {
    if (this.currentProcedureId) {
      return this.planCoreSvc.getByProcedureIdAndName(this.currentProcedureId, name);
    } else {
      return of([]);
    }
  };
  planLabelFn = (p: Plan) => `${p.code} - ${p.name}`;

  planStepSearchFn = (name?: string) => {
    const planId = this.localForm.value.plan?.id || this.currentPlanId;
    if (planId) {
      return this.planCoreSvc.getStepByPlanIdAndName(planId, name).pipe(
        map(steps => steps.filter(s => s.id !== this.currentPlanStepId as number && s.type === PlanStepType.procedure))
      );
    } else {
      return of([]);
    }
  };

  procedureStepSearchFn = (name?: string) => {
    if (this.localForm.value.planStep) {
      return this.procedureCoreSvc.getStepsByPlanStepIdAndName(this.localForm.value.planStep.id, name);
    } else {
      return this.procedureCoreSvc.getStepsByProcedureIdAndName(this.currentProcedureId as number, name).pipe(
        map(steps => steps.filter(s => s.id !== this.currentProcedureStepId as number))
      );
    }
  };

  localForm = this.fb.group({
    plan: this.fb.control(undefined, []),
    planStep: this.fb.control(undefined, []),
    procedureStep: this.fb.control(undefined, []),
    target: this.fb.control(undefined, [Validators.required]),
    key: this.fb.control(undefined, [Validators.required])
  });
  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private translocoSvc: TranslocoService,
    private planCoreSvc: PlanCoreService,
    private procedureCoreSvc: ProcedureCoreService
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnInit() {
    this.subs.add(
      this.localForm.get('plan')?.valueChanges.subscribe(() => {
        this.planStepCtl.clear();
        this.procedureStepCtl.clear();
        this.localForm.patchValue({ target: undefined, key: undefined }, { emitEvent: false });
      })
    );
    this.subs.add(this.localForm.get('planStep')?.valueChanges.subscribe(() => {
      this.procedureStepCtl.clear();
      this.localForm.patchValue({ target: undefined, key: undefined }, { emitEvent: false });
    }));
    this.subs.add(this.localForm.get('procedureStep')?.valueChanges.subscribe(() => {
      this.localForm.patchValue({ target: undefined, key: undefined }, { emitEvent: false });
    }));
    this.subs.add(this.localForm.get('target')?.valueChanges.subscribe(() => {
      this.localForm.patchValue({ key: undefined }, { emitEvent: false });
    }));
  }

  addCfg({ plan, planStep, procedureStep, key, target }: LocalFormValue) {
    this.configAdded.emit({
      planId: plan?.id,
      planLabel: plan ? `${plan.code} - ${plan.name}` : undefined,
      planStepId: planStep?.id,
      planStepLabel: planStep ? `${planStep.name}` : undefined,
      procedureStepId: procedureStep ? procedureStep.id as number : undefined,
      procedureStepLabel: procedureStep ? procedureStep.name : undefined,
      key,
      target,
    });

    if (this.localForm.dirty) {
      this.resetForm(true);
    }

  }

  resetForm(emitEvent: boolean) {
    this.localForm.reset({}, { emitEvent });
    this.localFormEl.resetForm();
    if (this.planCtl) {
      this.planCtl.clear();
    }
    if (this.planStepCtl) {
      this.planStepCtl.clear();
    }
    if (this.procedureStepCtl) {
      this.procedureStepCtl.clear();
    }
  }

  removeCfg(index: number) {
    this.configRemoved.emit(index);
  }

  getCfgLabel({ planLabel, planStepLabel, procedureStepLabel, target, key }: DataCollectorConfig) {
    return [planLabel, planStepLabel, procedureStepLabel, target, key].filter(x => !!x).join(' - ');
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.configs) {
      this.configs$$.next(changes.configs.currentValue);
    }
  }
  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
